import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { useMemo, useState } from 'react';

import { DashedTag, SearchBar } from '@/components/chariot-ui';
import { Dropdown } from '@/components/Dropdown';
import { FormGroup } from '@/components/form/FormGroup';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetAgents } from '@/hooks/useAgents';
import { getOsIcon } from '@/sections/agents/utils/osIcons';

interface AgentSelectorProps {
  selectedAgent: string;
  onAgentChange: (agent: string) => void;
}

/**
 * AgentSelector - Component for selecting Aegis agents for internal capabilities
 *
 * Provides a searchable dropdown to select from available online agents.
 * Selected agent is displayed as a removable tag below the search input.
 */
export function AgentSelector({
  selectedAgent,
  onAgentChange,
}: AgentSelectorProps) {
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);

  const { data: agentResults, status: agentStatus } = useGetAgents({
    filter: {
      search: searchValue,
      status: ['online'],
    },
  });

  const searchOptions = useMemo(() => {
    if (!agentResults) return [];

    return agentResults.slice(0, 50).map(agent => ({
      value: agent.client_id,
      label: `${agent.hostname} (${agent.fqdn}) - ${agent.client_id}`,
      id: agent.client_id,
      os: agent.os,
    }));
  }, [agentResults]);

  const isLoading = agentStatus === 'pending';

  const selectedAgentLabel = useMemo(() => {
    if (!selectedAgent || !agentResults) return '';
    const agent = agentResults.find(a => a.client_id === selectedAgent);
    return agent ? agent.hostname : selectedAgent;
  }, [selectedAgent, agentResults]);

  return (
    <FormGroup label="Select Endpoint" name="agent-selector">
      <div className="space-y-3">
        {isLoading ? (
          <Skeleton />
        ) : (
          <div className="relative">
            {/*
             * The SearchBar and the visible chevron button are intentionally
             * siblings of the Dropdown, NOT the Radix DropdownMenuTrigger child.
             * When the input lives inside the trigger, Radix's Slot makes the
             * trigger the keyboard target and its composed onKeyDown calls
             * preventDefault on Space/Enter/ArrowDown, swallowing characters
             * (e.g. the space in a search query) before they reach the input.
             * Keeping the SearchBar outside the trigger lets every keystroke
             * reach it. The Radix trigger is instead a zero-size, FULL-WIDTH
             * anchor (inset-x-0) so that fullWidthMenu measures the input's
             * width (var(--radix-dropdown-menu-trigger-width)) rather than the
             * narrow icon — if the chevron were the trigger the menu would size
             * to ~40px and truncate agent labels. keepFocusOnOpen stops the menu
             * from pulling focus off the SearchBar, and fullWidthMenu anchors it
             * under the input. Typing a non-empty query opens the menu
             * (search-as-you-type) and clearing the field closes it, while the
             * chevron remains a manual toggle of the controlled `open` state. A
             * lone Space keystroke (trimmed to empty) never opens the menu.
             */}
            <SearchBar
              size="sm"
              className="h-fit w-full"
              placeholder="Search Endpoints..."
              value={searchValue}
              onChange={value => {
                setSearchValue(value);
                setOpen(value.trim().length > 0);
              }}
            />
            <button
              type="button"
              aria-label="Show agents"
              onClick={() => setOpen(prev => !prev)}
              className="text-chariot-text-secondary absolute inset-y-0 right-0 flex items-center px-3"
            >
              <ChevronDownIcon className="size-4" />
            </button>
            <Dropdown
              menu={{
                items: searchOptions.map(option => ({
                  label: option.label,
                  icon: getOsIcon(option.os),
                  onClick: () => onAgentChange(option.value),
                })),
                emptyState: {
                  hide: searchOptions.length > 0 || !searchValue,
                  label: isLoading ? 'Loading...' : 'No agents found',
                },
                className: 'max-h-[300px]',
                open,
                onOpenChange: setOpen,
                fullWidthMenu: true,
              }}
              keepFocusOnOpen
              asChild
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-0"
              />
            </Dropdown>
          </div>
        )}

        {selectedAgent && (
          <div className="flex items-center gap-1">
            <DashedTag
              icon={<XCircleIcon className="size-5" />}
              className="text-chariot-text-secondary py-1"
              size="sm"
              onClick={() => onAgentChange('')}
              label={selectedAgentLabel}
            />
          </div>
        )}
      </div>
    </FormGroup>
  );
}
